// Copyright (c) StrangeBee 2023

import React, { ReactElement, useState } from 'react';
import { useLocalMessage } from '@Helpers/locale';
import RouteBuilder from '@Services/RouteBuilder';
import { THLayout } from '@Molecules/THLayout';
import { useGenericQuery } from '@Queries/genericQueries';
import client from '@Client';
import { useEffect } from 'react';
import { TitleSection } from '@Atoms/TitleSection/TitleSection';
import { RouteType } from '@Types';
import { THPassword } from '@Molecules/Inputs/THPassword/THPassword';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import { THForm } from '@Molecules/THForm/THForm';
import { THSwitch } from '@Molecules/Inputs/THSwitch/THSwitch';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import { THInputNumber } from '@Molecules/Inputs/THInputNumber/THInputNumber';
import { handleNotificationError, openMessage } from '@Helpers/';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { PermissionsEnum } from 'thehive-sdk';
import useValidateForm from '@Hooks/useValidateForm';
import { ICONS } from '@Constants/iconsApp';

import './MailerDetails.scss';
import { THNumberSelect } from '@Molecules/Inputs/THNumberSelect/THNumberSelect';

const nameKey = {
    user: 'user',
    password: 'password',
    host: 'host',
    port: 'port',
    from: 'from',
    expiration: 'expiration',
    security: 'security',
    tlsRequired: 'tlsRequired',
};

const emptyStringToNull = (data?: Record<string, any>): Record<string, any> =>
    Object.entries({ ...data }).reduce((acc, [key, value]) => {
        if (typeof value == 'string' && value.trim() == '') {
            value = null;
        }
        return {
            ...acc,
            [key]: value,
        };
    }, {});

const MailerDetails = (): ReactElement => {
    const i = useLocalMessage();

    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm();
    const [configStarted, setConfigStarted] = useState<Record<string, any>>({});
    const [isTls, setIsTls] = useState(false);
    const [valuePort, setValuePort] = useState(25);

    const { data: mailerConfig } = useGenericQuery({
        fetchCallback: () => client.MailerController.getConfig(),
        key: ['get-mailer-config'],
    });

    const { data: forgotPasswordConfig } = useGenericQuery({
        fetchCallback: () => client.ForgetPasswordController.getConfig(),
        key: ['get-forgot-password-config'],
    });

    useEffect(() => {
        if (mailerConfig && forgotPasswordConfig) {
            const { from, expiration } = forgotPasswordConfig;
            const { ssl, tls, port, tlsRequired, ...rest } = mailerConfig;
            let portTmp = port;
            let tlsRequiredTmp = tlsRequired;
            const security = ssl ? 'ssl' : tls ? 'tls' : 'none';
            onChangeSecurity(security);

            if (security === 'ssl') {
                portTmp = port === 25 ? 465 : port;
                tlsRequiredTmp = false;
            }

            setValuePort(portTmp);
            setIsTls(security === 'tls');

            setConfigStarted({ ...rest, security, port: portTmp, from, expiration, tlsRequired: tlsRequiredTmp });
        }
    }, [mailerConfig, forgotPasswordConfig]); //eslint-disable-line

    const onFinishHandler = (state: any) => {
        const treatForm = emptyStringToNull(state);

        const treatFormFrom = treatForm.from;
        const treatFormExpiration = treatForm.expiration;

        treatForm.ssl = false;
        treatForm.tls = false;

        if (treatForm.security === 'ssl') {
            treatForm.ssl = true;
            treatForm.tlsRequired = false;
        }
        if (treatForm.security === 'tls') {
            treatForm.tls = true;
        }
        if (treatForm.security === 'none') {
            treatForm.tlsRequired = false;
        }

        delete treatForm.security;
        delete treatForm.from;
        delete treatForm.expiration;

        Promise.all([
            client.MailerController.edit({ ...mailerConfig, ...treatForm }),
            client.ForgetPasswordController.edit({
                ...forgotPasswordConfig,
                from: treatFormFrom,
                expiration: treatFormExpiration,
            }),
        ])
            .then(() => {
                openMessage('success', i('smtp.message.success'));
            })
            .catch(handleNotificationError);

        setFieldsTouched(false);
    };

    const onChangePort = (value: any) => {
        setValuePort(value);
    };

    const onChangeSecurity = (value: string) => {
        setIsTls(value === 'tls');
        const formDataClone = form.getFieldsValue(true);

        if (value === 'ssl') {
            if (valuePort === 25) {
                onChangePort(465);
                form.setFieldsValue({ ...formDataClone, [nameKey.port]: 465 });
            }
        } else {
            if (valuePort === 465) {
                onChangePort(25);
                form.setFieldsValue({ ...formDataClone, [nameKey.port]: 25 });
            }
        }
    };

    const onResetForm = () => {
        const { ssl, tls } = configStarted;
        const security = ssl ? 'ssl' : tls ? 'tls' : 'none';
        setIsTls(security === 'tls');

        setFieldsTouched(false);
        form.resetFields();
    };

    useEffect(() => {
        form.resetFields();
        // eslint-disable-next-line
    }, [configStarted]);

    return (
        <THLayout>
            <THLayout.Content className="mailer-layout">
                <THForm
                    form={form}
                    autoComplete="off"
                    onFinish={onFinishHandler}
                    initialValues={configStarted}
                    onValuesChange={() => {
                        onFormValuesChange();
                        setFieldsTouched(true);
                    }}
                >
                    <THForm.Layout
                        footer={
                            <FormSubmitButton
                                warning
                                disabled={!isValid}
                                onCancel={onResetForm}
                                hidden={!fieldsTouched}
                            />
                        }
                    >
                        <div className="mailer-details__basic-informations">
                            <TitleSection>{i('smtp.settings')}</TitleSection>
                            <THForm.Item
                                name={nameKey.host}
                                label={i('smtp.fields.host')}
                                rules={[{ required: true, message: i('smtp.required.host') }]}
                                className="input-half"
                            >
                                <THInput />
                            </THForm.Item>

                            <THForm.Item
                                name={nameKey.port}
                                label={i('smtp.fields.port')}
                                className="input-half"
                                rules={[{ required: true, message: i('smtp.required.port') }]}
                            >
                                <THInputNumber onChange={onChangePort} />
                            </THForm.Item>
                        </div>
                        <div className="mailer-details__">
                            <TitleSection>{i('smtp.resetPassword')}</TitleSection>

                            <THForm.Item
                                name={nameKey.from}
                                rules={[{ required: true, type: 'email', message: i('smtp.required.from') }]}
                                label={i('smtp.fields.from')}
                                className="input-half"
                            >
                                <THInput />
                            </THForm.Item>

                            <THForm.Item
                                name={nameKey.expiration}
                                label={i('smtp.fields.tokenExpiration')}
                                className="auth-input__duration input-half"
                            >
                                <THNumberSelect options={['seconds', 'minutes', 'hours']} />
                            </THForm.Item>
                        </div>
                        <div className="mailer-details__connection">
                            <TitleSection>{i('smtp.connexion')}</TitleSection>

                            <div className="mailer-details__security input-half">
                                <THForm.Item label={i('smtp.fields.security')} name={nameKey.security}>
                                    <THSelect
                                        options={[
                                            { label: i('smtp.fields.none'), value: 'none' },
                                            { label: i('smtp.fields.ssl'), value: 'ssl' },
                                            { label: i('smtp.fields.starttls'), value: 'tls' },
                                        ]}
                                        onChange={onChangeSecurity}
                                    />
                                </THForm.Item>
                                <THForm.Item
                                    name={nameKey.tlsRequired}
                                    hidden={!isTls}
                                    label={i('smtp.fields.tlsRequired')}
                                    valuePropName="checked"
                                    className="mailer-details__security-tlsrequired"
                                >
                                    <THSwitch />
                                </THForm.Item>
                            </div>

                            <THForm.Item name={nameKey.user} label={i('smtp.fields.user')} className="input-half">
                                <THInput autoComplete="new-password" />
                            </THForm.Item>

                            <THForm.Item
                                name={nameKey.password}
                                label={i('smtp.fields.password')}
                                className="input-half"
                            >
                                <THPassword autoComplete="new-password" />
                            </THForm.Item>
                        </div>
                    </THForm.Layout>
                </THForm>
            </THLayout.Content>
        </THLayout>
    );
};

const path = RouteBuilder.compute('administration.mailer');
export const Route: RouteType = {
    path,
    menu: {
        label: 'mainLayout.adminMenu.mailer',
        icon: <ICONS.InboxOutlined />,
    },
    breadcrumb: { label: 'breadcrumb.global.mailer' },
    content: <MailerDetails />,
    permissionNeeded: [PermissionsEnum.MANAGE_CONFIG],

    space: ['admin'],
    exact: true,
};
